// AppDatePicker.js
// The platform's only date input.
//
// TWO FIXED FACTS MEET HERE. Every date this platform stores is a 'YYYY-MM-DD'
// string — Firestore filter values, a range filter's `gte`/`lte`, and the
// parameters sent with a function run are all read by the backend in exactly
// that shape. Every customer is UK-based and reads a date as DD/MM/YYYY. A
// native <input type="date"> cannot serve both: it renders in the BROWSER's
// locale, which is commonly mm/dd/yyyy, and offers no way to override that.
//
// So the conversion lives here and nowhere else. 'YYYY-MM-DD' in, DD/MM/YYYY on
// screen, 'YYYY-MM-DD' back out; call sites keep the string contract they
// already had and never handle a dayjs object.
//
// A HALF-TYPED DATE IS NOT A DATE. Typing "05" into an empty field leaves the
// month and year unfilled, and there is no honest 'YYYY-MM-DD' for that, so
// every incomplete or invalid entry emits '' — an unset filter — rather than a
// guess. The user's typing survives this because the value they are editing is
// not the value coming back down: the field keeps its own section state until
// the value from above genuinely changes.
//
// But the draft must not pass for an absent filter at submit time. onChange
// carries `{ partial }` alongside the value, and inside a DateDraftScope the
// picker reports its partial state so the host's Apply / Run can refuse and
// turn the field red (see DateDraftScope.js).

import React, { useContext, useEffect, useId, useState } from 'react';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateDraftContext } from './DateDraftScope';

// What the backend reads and writes. Also what dayjs parses by default, and in
// LOCAL time for a date-only string, so a value round-trips to the same day it
// arrived as regardless of the browser's offset.
const WIRE_FORMAT = 'YYYY-MM-DD';

// What a UK customer reads.
const DISPLAY_FORMAT = 'DD/MM/YYYY';

const INCOMPLETE_DATE_HELPER = 'Enter a complete date (DD/MM/YYYY) or clear it';

function AppDatePicker({
  label,
  value,
  onChange,
  disabled = false,
  fullWidth = false,
  size = 'small',
  slotProps,
  ...pickerProps
}) {
  // '' and null both mean "no date"; anything unparseable is treated the same
  // way rather than rendered as an error the user cannot act on.
  const parsed = value ? dayjs(value) : null;
  const dateValue = parsed && parsed.isValid() ? parsed : null;

  // MUI reports 'invalidDate' the moment the field is neither empty nor a
  // complete date, and a field emptied section by section as null with no
  // error — so this is exactly "there is a draft on screen".
  const [partial, setPartial] = useState(false);
  const drafts = useContext(DateDraftContext);
  const report = drafts?.report;
  const draftId = useId();

  // A changed value from above re-syncs the field's sections to it (the draft
  // only survives while the value above stays the same), so whatever is on
  // screen afterwards is complete or empty — never partial. Keyed on the
  // parsed value, as the field's own sync is: '', null and undefined are one
  // and the same unset value, not a change.
  const wireValue = dateValue ? dateValue.format(WIRE_FORMAT) : '';
  useEffect(() => {
    setPartial(false);
  }, [wireValue]);

  useEffect(() => {
    if (!report) return undefined;
    report(draftId, partial);
    return () => report(draftId, false);
  }, [report, draftId, partial]);

  const handleChange = (nextDate, context) => {
    const isPartial = context?.validationError === 'invalidDate';
    setPartial(isPartial);
    onChange(nextDate && nextDate.isValid() ? nextDate.format(WIRE_FORMAT) : '', { partial: isPartial });
  };

  const showIncomplete = partial && Boolean(drafts?.revealed);

  return (
    <DatePicker
      label={label}
      value={dateValue}
      onChange={handleChange}
      disabled={disabled}
      format={DISPLAY_FORMAT}
      {...pickerProps}
      slotProps={{
        ...slotProps,
        // Once a date has been picked from the calendar, the clear button is the
        // only way back to an unset filter without emptying each section by hand.
        field: { clearable: true, ...(slotProps?.field || {}) },
        // A call site's own textField props win, so a site that needs `error`,
        // `helperText`, `autoFocus` or its own `sx` passes them through unchanged.
        textField: {
          fullWidth,
          size,
          ...(showIncomplete ? { error: true, helperText: INCOMPLETE_DATE_HELPER } : {}),
          ...(slotProps?.textField || {}),
        },
      }}
    />
  );
}

export default AppDatePicker;
